import { Box, Card, Group, Select, Text, TextInput, LoadingOverlay } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useState, useMemo, useCallback, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import RefreshQueryButton from "@/components/refresh-query-button";
import { TemplateList, TemplateActions, TemplateConfirmDialog } from "@/modules/relatorios/components/templates";
import { type TemplateFormData, AvailableTemplates } from "@/modules/relatorios/forms";
import { useTemplatesManager, TEMPLATES_QUERY_KEY } from "@/modules/relatorios/hooks";
import type { TemplateListItem } from "@/modules/relatorios/types/template";

type StatusFilter = "all" | "active" | "inactive";

const STATUS_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "active", label: "Ativos" },
  { value: "inactive", label: "Inativos" }
];

const QUERY_KEY = [TEMPLATES_QUERY_KEY];

/**
 * Admin page for managing report templates
 *
 * Follows the visual pattern of EmpresaPage with Card sections
 */
export function TemplatesPage() {
  const navigate = useNavigate();

  // Filter state - inline (sem drawer)
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  // Selected template state
  const [editingTemplate, setEditingTemplate] = useState<TemplateListItem | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<TemplateListItem | null>(null);

  // Hook do Firestore
  const {
    templates,
    isLoading,
    createTemplate,
    updateTemplate,
    toggleActive,
    deleteTemplate,
    isCreating,
    isUpdating,
    isDeleting
  } = useTemplatesManager();

  // Filter templates based on search and status
  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((template) => template.nome.toLowerCase().includes(query));
    }

    // Apply status filter
    if (statusFilter === "active") {
      filtered = filtered.filter((template) => template.ativo);
    } else if (statusFilter === "inactive") {
      filtered = filtered.filter((template) => !template.ativo);
    }

    return filtered;
  }, [searchQuery, statusFilter, templates]);

  const hasActiveFilters = searchQuery || statusFilter !== "all";

  // Handlers
  const handleCreate = useCallback(
    async (data: TemplateFormData) => {
      await createTemplate(data);
    },
    [createTemplate]
  );

  const handleUpdate = useCallback(
    async (id: string, data: TemplateFormData) => {
      await updateTemplate({ templateId: id, data });
      setEditingTemplate(null);
    },
    [updateTemplate]
  );

  const handleClearEdit = useCallback(() => {
    setEditingTemplate(null);
  }, []);

  const handleToggleActive = useCallback(
    (template: TemplateListItem) => {
      void toggleActive({ templateId: template.id, ativo: !template.ativo });
    },
    [toggleActive]
  );

  const handleDelete = useCallback(() => {
    if (!deletingTemplate) return;
    void deleteTemplate(deletingTemplate.id);
    setDeletingTemplate(null);
  }, [deletingTemplate, deleteTemplate]);

  const handleClearDelete = useCallback(() => {
    setDeletingTemplate(null);
  }, []);

  const handleCopy = useCallback(
    (template: TemplateListItem) => {
      const timestamp = new Date().toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });

      const copyData: TemplateFormData = {
        nome: `Cópia de ${template.nome} - ${timestamp}`,
        descricao: template.descricao || "",
        componentPath: (template.componentPath as AvailableTemplates) || AvailableTemplates.DEFAULT,
        ativo: true,
        acessivelConvidada: template.acessivelConvidada ?? false,
        configuracao: {
          incluirGraficos: true,
          incluirTabelas: true,
          incluirAnaliseDetalhada: true
        },
        htmlTemplate: template.htmlTemplate
      };

      void createTemplate(copyData);
    },
    [createTemplate]
  );

  const handleEditHtml = useCallback(
    (template: TemplateListItem) => {
      navigate(`/configuracoes/diagnostico/templates/${template.id}/editor`);
    },
    [navigate]
  );

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.currentTarget.value);
  }, []);

  const handleStatusChange = useCallback((value: string | null) => {
    setStatusFilter((value as StatusFilter) || "all");
  }, []);

  return (
    <Card
      withBorder
      shadow="md"
      styles={{
        section: {
          overflow: "auto"
        }
      }}
    >
      {/* Header Section - Reorganizado */}
      <Card.Section inheritPadding py="lg" withBorder>
        <Group justify="space-between" align="flex-start">
          <Box>
            <Text fw={600} fz={24} lh={1.2}>
              Templates de Relatórios
            </Text>
            <Text size="sm" c="dimmed" mt={4}>
              {filteredTemplates.length} {filteredTemplates.length === 1 ? "template" : "templates"}
              {hasActiveFilters && ` (de ${templates.length} total)`}
            </Text>
          </Box>
          <TemplateActions
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            editingTemplate={editingTemplate}
            onClearEdit={handleClearEdit}
            isSubmitting={isCreating || isUpdating}
          />
        </Group>
      </Card.Section>

      {/* Filters Section - Inline */}
      <Card.Section inheritPadding py="md" withBorder bg="gray.0">
        <Group gap="md">
          <TextInput
            placeholder="Buscar templates..."
            leftSection={<IconSearch size="1rem" />}
            value={searchQuery}
            onChange={handleSearchChange}
            flex={1}
            styles={{ input: { backgroundColor: "white" } }}
          />
          <Select
            placeholder="Status"
            data={STATUS_OPTIONS}
            value={statusFilter}
            onChange={handleStatusChange}
            w={140}
            clearable
            styles={{ input: { backgroundColor: "white" } }}
          />
          <RefreshQueryButton queryKey={QUERY_KEY} />
        </Group>
      </Card.Section>

      {/* Table Section */}
      <Card.Section inheritPadding py="md" pos="relative">
        <LoadingOverlay visible={isLoading} />
        <TemplateList
          templates={filteredTemplates}
          onEdit={setEditingTemplate}
          onDelete={setDeletingTemplate}
          onToggleActive={handleToggleActive}
          onCopy={handleCopy}
          onEditHtml={handleEditHtml}
          isLoading={isLoading || isCreating || isUpdating || isDeleting}
        />
      </Card.Section>

      {/* Footer Section - Simplificado */}
      <Card.Section inheritPadding py="sm" withBorder bg="gray.0">
        <Text size="xs" c="dimmed" ta="center">
          {filteredTemplates.length} {filteredTemplates.length === 1 ? "template encontrado" : "templates encontrados"}
        </Text>
      </Card.Section>

      {/* Delete Confirmation Dialog */}
      <TemplateConfirmDialog
        opened={!!deletingTemplate}
        onClose={handleClearDelete}
        onConfirm={handleDelete}
        template={deletingTemplate}
        isDeleting={isDeleting}
      />
    </Card>
  );
}
